import React from 'react';
import { motion, useInView } from 'framer-motion';
import { cn } from '../../lib/utils';
import {
  ChevronRight,
  FileText,
  Search,
  Check,
  TrendingUp,
} from 'lucide-react';
import WingmanMockup from '../../assets/wingman.png';
import { AnimatedDashboard } from '../../components/dashboard-card/AnimatedDashboard';

interface ProductSectionProps {
  className?: string;
}

export const ProductSection: React.FC<ProductSectionProps> = ({
  className,
}) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10%',
    amount: 0.1,
  });

  // Animation variants - Less sensitive/jerky
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1], // Smoother easing
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1], // Smoother, less bouncy easing
      },
    },
  };

  const titleVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1], // Consistent smooth easing
      },
    },
  };

  return (
    <motion.section
      id="product"
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-t-3xl py-12 max-[599px]:rounded-t-none max-[599px]:py-16 lg:py-20',
        className
      )}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {/* Subtle Abstract Shapes - Corner Gradient Blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-t-3xl max-[599px]:rounded-t-none">
        {/* Top-left blue gradient blob */}
        <div className="absolute -top-20 -left-20 h-[400px] w-[400px] rounded-full bg-gradient-to-br from-blue-600/12 via-blue-500/8 to-transparent blur-3xl"></div>

        {/* Top-right blue gradient blob */}
        <div className="absolute -top-20 -right-20 h-[350px] w-[350px] rounded-full bg-gradient-to-bl from-blue-600/10 via-blue-500/6 to-transparent blur-3xl"></div>

        {/* Bottom-left blue gradient blob */}
        <div className="absolute -bottom-20 -left-20 h-[450px] w-[450px] rounded-full bg-gradient-to-tr from-blue-600/11 via-blue-500/7 to-transparent blur-3xl"></div>

        {/* Bottom-right blue gradient blob */}
        <div className="absolute -right-20 -bottom-20 h-[300px] w-[300px] rounded-full bg-gradient-to-tl from-blue-600/9 via-blue-500/5 to-transparent blur-3xl"></div>

        {/* Center accent blob for depth */}
        <div className="absolute top-1/2 left-1/2 h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-500/8 via-blue-400/6 to-blue-300/4 blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div className="mb-16 text-center" variants={titleVariants}>
          <motion.h2
            className="font-primary text-4xl font-bold text-slate-900 max-[599px]:text-5xl lg:text-5xl"
            variants={titleVariants}
          >
            What do i get?
          </motion.h2>
        </motion.div>

        {/* Bento Grid Layout */}
        <motion.div
          className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-2"
          variants={containerVariants}
        >
          {/* Left Column - Two Small Cards */}
          <motion.div
            className="flex flex-col gap-6 lg:col-span-1"
            variants={itemVariants}
          >
            {/* Card 1 - Dashboard */}
            <motion.div
              className="group relative h-full"
              variants={itemVariants}
            >
              {/* Glassmorphism Card */}
              <div className="relative h-full min-h-[400px] overflow-hidden rounded-3xl border border-white/30 bg-white/60 p-0 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-[8px] backdrop-saturate-180 transition-all duration-500 hover:scale-[1.02] hover:bg-white/70 hover:shadow-[0_8px_12px_rgba(0,0,0,0.15),0_2px_4px_rgba(0,0,0,0.1)] max-[599px]:min-h-[350px]">
                {/* Purple gradient overlay */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-purple-600/15 via-transparent to-transparent"></div>

                {/* Content */}
                <div className="relative z-10 flex h-full max-[599px]:flex-col">
                  {/* Left side - Text content */}
                  <div className="flex w-1/2 flex-col justify-center p-8 max-[599px]:w-full max-[599px]:pb-4">
                    <h3 className="mb-2 text-3xl font-bold text-slate-900 max-[599px]:text-center">
                      Wingman
                    </h3>
                    <p className="text-sm text-slate-600 max-[599px]:text-center">
                      for Real-Time decision making
                    </p>
                  </div>

                  {/* Right side - Mockup */}
                  <div className="relative w-1/2 overflow-hidden max-[599px]:flex max-[599px]:w-full max-[599px]:justify-center">
                    <img
                      src={WingmanMockup}
                      alt="Dashboard Mockup"
                      className="absolute right-0 bottom-0 h-full w-auto object-contain object-bottom max-[599px]:relative max-[599px]:right-auto max-[599px]:bottom-auto max-[599px]:h-auto max-[599px]:max-h-[250px] max-[599px]:w-full"
                      style={{ maxHeight: '100%' }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Card 2 - Supervisor */}
            <motion.div
              className="group relative h-full"
              variants={itemVariants}
            >
              {/* Glassmorphism Card with Light Blue Gradient Background */}
              <div
                className="relative h-full overflow-visible rounded-3xl border border-white/30 p-8 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-[8px] backdrop-saturate-180 transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_8px_12px_rgba(0,0,0,0.15),0_2px_4px_rgba(0,0,0,0.1)] max-[599px]:p-4"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(230, 243, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 50%, rgba(230, 243, 255, 0.6) 100%)',
                }}
              >
                {/* Light blue gradient overlay */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-3xl"
                  style={{
                    background:
                      'linear-gradient(-45deg, rgba(230, 243, 255, 0.3) 0%, transparent 70%)',
                  }}
                ></div>

                {/* Content */}
                <div className="relative z-10 flex h-full flex-col">
                  {/* Title at Top */}
                  <div className="mb-6 max-[599px]:text-center">
                    {/* Desktop: Single line heading */}
                    <h3 className="text-xl font-semibold text-slate-900 max-[599px]:hidden">
                      Supervisor that monitors your business
                    </h3>
                    {/* Mobile: Main heading + subheading structure */}
                    <div className="hidden max-[599px]:block">
                      <h3 className="mb-2 text-3xl font-bold text-slate-900">
                        Supervisor
                      </h3>
                      <p className="text-sm text-slate-600">
                        that monitors your business
                      </p>
                    </div>
                  </div>

                  {/* App-like Interface Container */}
                  <div className="flex-1 rounded-2xl border border-white/50 bg-white/95 p-6 px-8 shadow-[0_8px_40px_rgba(0,0,0,0.12)] backdrop-blur-xl max-[599px]:p-4 max-[599px]:px-5">
                    {/* App Header */}
                    <div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-4 max-[599px]:mb-4 max-[599px]:pb-3">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-6 w-6 text-gray-700 max-[599px]:h-5 max-[599px]:w-5" />
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 max-[599px]:text-lg">
                            Reports
                          </h4>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Search className="h-5 w-5 text-gray-400 max-[599px]:h-4 max-[599px]:w-4" />
                      </div>
                    </div>

                    <div className="flex h-full flex-col space-y-3 max-[599px]:space-y-2">
                      {/* Monthly Sales Card - App style */}
                      <div className="group flex min-h-[80px] cursor-pointer items-center justify-between rounded-xl border border-green-200/50 bg-gradient-to-r from-green-50/80 to-emerald-50/80 p-4 pr-6 shadow-sm transition-all duration-200 hover:from-green-100/80 hover:to-emerald-100/80 max-[599px]:min-h-[60px] max-[599px]:p-3 max-[599px]:pr-4">
                        <div className="flex items-center space-x-4 max-[599px]:space-x-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-500/10 max-[599px]:h-8 max-[599px]:w-8 max-[599px]:rounded-lg">
                            <Check className="h-5 w-5 text-green-600 max-[599px]:h-4 max-[599px]:w-4" />
                          </div>
                          <div className="flex-1">
                            <h4 className="mb-1 text-lg font-bold text-slate-900 max-[599px]:mb-0.5 max-[599px]:text-sm">
                              Monthly sales analysis
                            </h4>
                            <p className="text-xs text-slate-600 max-[599px]:text-[10px] max-[599px]:leading-tight">
                              Looks like this month we've met the projected
                              sales targets. Kudos!
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="ml-4 h-5 w-5 text-slate-400 transition-colors duration-200 group-hover:text-slate-600 max-[599px]:ml-2 max-[599px]:h-4 max-[599px]:w-4" />
                      </div>

                      {/* Weekly Expenditure Card - App style */}
                      <div className="group flex min-h-[80px] cursor-pointer items-center justify-between rounded-xl border border-red-200/50 bg-gradient-to-r from-red-50/80 to-orange-50/80 p-4 pr-6 shadow-sm transition-all duration-200 hover:from-red-100/80 hover:to-orange-100/80 max-[599px]:min-h-[60px] max-[599px]:p-3 max-[599px]:pr-4">
                        <div className="flex items-center space-x-4 max-[599px]:space-x-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-500/10 max-[599px]:h-8 max-[599px]:w-8 max-[599px]:rounded-lg">
                            <TrendingUp className="h-5 w-5 text-red-600 max-[599px]:h-4 max-[599px]:w-4" />
                          </div>
                          <div className="flex-1">
                            <h4 className="mb-1 text-lg font-bold text-slate-900 max-[599px]:mb-0.5 max-[599px]:text-sm">
                              Weekly expenditure report
                            </h4>
                            <p className="text-xs text-slate-600 max-[599px]:text-[10px] max-[599px]:leading-tight">
                              Rate of increase in expense is 20% higher than
                              budget. Let's take a look.
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="ml-4 h-5 w-5 text-slate-400 transition-colors duration-200 group-hover:text-slate-600 max-[599px]:ml-2 max-[599px]:h-4 max-[599px]:w-4" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Large Card */}
          <motion.div className="lg:col-span-1" variants={itemVariants}>
            <motion.div
              className="group relative h-full"
              variants={itemVariants}
            >
              {/* Isolation wrapper to prevent animation from affecting page scroll */}
              <div style={{ isolation: 'isolate', contain: 'layout' }}>
                <AnimatedDashboard />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};
